import { Typography, useTheme } from 'hephaestus/web';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { useBadgeTokens } from './badge.tokens';

export const colorMap = {
  neutral: 'neutral',
  negative: 'negative',
  warning: 'warning',
  positive: 'positive',
  gray: 'gray',
  supportOne: 'supportOne',
  supportTwo: 'supportTwo',
  supportThree: 'supportThree',
  supportFour: 'supportFour',
  supportFive: 'supportFive',
  supportSix: 'supportSix',
};

export type BadgeProps = {
  label: string;
  color?: keyof typeof colorMap;
  variant?: 'number' | 'text';
  sx?: any;
  testId?: string;
} & Omit<ComponentPropsWithoutRef<'span'>, 'color'>;

/**
 * Forwards ref and extra props to the root `span` so wrappers such as `Tooltip`
 * can attach to it directly.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      label,
      color = 'neutral',
      variant = 'text',
      sx = {},
      testId = 'badge',
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const tokens = useBadgeTokens(theme);
    const textColor =
      variant === 'number'
        ? tokens.number.textColor
        : tokens[variant][color].textColor;
    const bgColor =
      variant === 'number'
        ? tokens.number.backgroundColor
        : tokens[variant][color].backgroundColor;
    const borderColor =
      variant === 'number'
        ? tokens.number.borderColor
        : tokens[variant][color].borderColor;
    const borderStyle = tokens[variant].borderStyle;
    const borderWidth = tokens[variant].borderWidth;
    return (
      <span {...rest} ref={ref} data-testid={testId}>
        <Typography
          data-testid={`${testId}-text`}
          variant='labelSmall'
          sx={{
            minHeight: variant === 'number' ? '16px' : '20px',
            display: 'inline-block',
            textAlign: 'center',
            color: textColor,
            backgroundColor: bgColor,
            borderRadius: tokens[variant].borderRadius,
            borderStyle,
            borderWidth,
            borderColor,
            minWidth: '16px',
            alignContent: 'center',
            padding: '1px 4px 1px 4px',
            ...sx,
          }}
        >
          {label}
        </Typography>
      </span>
    );
  },
);
